import { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "../molecules/Header.jsx";
import QuitConfirmation from "../molecules/QuitConfirmation.jsx";
import SendMessage from "../molecules/SendMessage.jsx";
import MessagesContainer from "../molecules/MessagesContainer.jsx";
import QueuePosition from "../molecules/QueuePosition.jsx";
import RestartChatButton from "../atoms/buttons/RestartChatButton.jsx";
import RetryLiveAgentButton from "../atoms/buttons/RetryLiveAgentButton.jsx";
import { CHAT_SESSION_ID, END_MSG, GREETING_MSG_POST, GREETING_MSG_POST2, GREETING_MSG_POST3, GREETING_MSG_PRE, IS_LOGIN } from "../../../constants.js";
import { formatMessageData } from "../../utils/formatData.js";
import { clearRefresher, concatMessage, incrementPosition, setLoadingReply, setMessages, updateExpiredAt } from "../../features/messagesSlice.js";
import {
    setFloatingContainerState,
    setFloatingWindowState,
    setHeaderState,
    setSendMessageContainerState
} from "../../features/componentStateSlice.js";
import { setSurveyFormState } from "../../features/surveySlice.js";
import TypingText from "../atoms/texts/TypingText.jsx";
import { clearQueuePos, setLiveAgentUnavailable, setRetryLiveAgent } from "../../features/liveAgentSlice.js";
import { genesysEnd } from "../../services/liveAgentService.js";
import { endChat } from "../../services/chatService.js";

export default function FloatingWindow() {
    const dispatch = useDispatch();
    const windowContainer = useRef();
    const floatingWindowState = useSelector(state => state.componentState.floatingWindowState);
    const isTypingContainerState = useSelector(state => state.componentState.isTypingContainerState);
    const liveAgentUnavailable = useSelector(state => state.liveAgent.liveAgentUnavailable);
    const isEnd = useSelector(state => state.messages.isEnd);
    const messagesArchived = useSelector(state => state.messages.messagesArchived);
    const position = useSelector(state => state.messages.position);
    const queuePos = useSelector(state => state.liveAgent.queuePos);
    const quitConfirmationContainerState = useSelector(state => state.componentState.quitConfirmationContainerState);
    const sendMessageContainerState = useSelector(state => state.componentState.sendMessageContainerState);
    const headerState = useSelector(state => state.componentState.headerState);
    const token = useSelector((state) => state.session.token);
    const deviceId = useSelector((state) => state.session.deviceId);
    const genesysDetails = useSelector(state => state.liveAgent.genesysDetails);
    const queueScheduler = useSelector(state => state.liveAgent.queueScheduler);
    const messages = useSelector(state => state.messages.messages);
    const expiredScheduler = useSelector(state => state.messages.expiredScheduler);
    const connectedLiveAgent = useSelector(state => state.liveAgent.connectedLiveAgent);

    useEffect(() => {
        const containerElement = windowContainer.current;
        if (floatingWindowState) {
            containerElement.style.display = "flex";
        } else {
            containerElement.style.display = "none";
        }
    }, [floatingWindowState]);

    // Set Greeting Message
    useEffect(() => {
        if (CHAT_SESSION_ID !== null && token) {
            if (messagesArchived.length === 0) {
                if (IS_LOGIN) { // Post Login
                    const greetingMessage = formatMessageData(deviceId, "bot", GREETING_MSG_POST, position);
                    dispatch(concatMessage(greetingMessage));
                    dispatch(incrementPosition());
                    dispatch(updateExpiredAt());
                    setTimeout(() => {
                        const greetingMessage2 = formatMessageData(deviceId, "bot", GREETING_MSG_POST2, position);
                        dispatch(concatMessage(greetingMessage2));
                        dispatch(incrementPosition());
                        dispatch(updateExpiredAt());
                        setTimeout(() => {
                            const greetingMessage3 = formatMessageData(deviceId, "bot", GREETING_MSG_POST3, position);
                            dispatch(concatMessage(greetingMessage3));
                            dispatch(incrementPosition());
                            dispatch(updateExpiredAt());
                            dispatch(setLoadingReply(false));
                        }, 1000);
                    }, 1000);
                } else { // Pre Login
                    const greetingMessage = formatMessageData(deviceId, "bot", GREETING_MSG_PRE, position);
                    dispatch(concatMessage(greetingMessage));
                    dispatch(incrementPosition());
                    dispatch(updateExpiredAt());
                    dispatch(setLoadingReply(false));
                }
            }
        }
    }, [CHAT_SESSION_ID, token]);

    useEffect(() => {
        const handleEndChat = async () => {
            if (isEnd) {
                dispatch(setFloatingContainerState(true));
                dispatch(setFloatingWindowState(false));
                dispatch(setHeaderState(false));
                dispatch(setSendMessageContainerState(false));
                dispatch(setSurveyFormState(true));
                dispatch(clearRefresher());
                if (messages.every(msg => msg?.content !== END_MSG)) {
                    const message = formatMessageData(deviceId, "bot", END_MSG, position);
                    dispatch(setMessages([...messages, message]));
                    dispatch(incrementPosition());
                    clearInterval(queueScheduler);
                    clearInterval(expiredScheduler);
                    dispatch(clearQueuePos());
                    dispatch(setLiveAgentUnavailable(false));
                    dispatch(setRetryLiveAgent(false));
                    if (connectedLiveAgent) genesysEnd(genesysDetails.chatId, genesysDetails.userId, genesysDetails.secureKey, genesysDetails.alias);
                    await endChat(token);
                }
            }
        };
        handleEndChat();
    }, [isEnd]);

    return (
        <div>
            <div ref={ windowContainer } className="floating-window">
                <div className="floating-window-container">
                    { headerState && (
                        <Header/>
                    ) }
                    { quitConfirmationContainerState && (
                        <QuitConfirmation/>
                    ) }
                    <MessagesContainer/>
                    { queuePos && (
                        <QueuePosition/>
                    ) }
                    { (isTypingContainerState) && (
                        <TypingText/>
                    )}
                    { sendMessageContainerState && (
                        <SendMessage/>
                    ) }
                    { isEnd && (
                        <div className="restart-chat-button">
                            <RestartChatButton/>
                        </div>
                    ) }
                    { liveAgentUnavailable && (
                        <div className="retry-button">
                            <RetryLiveAgentButton/>
                        </div>
                    ) }
                </div>
            </div>
        </div>
    );
};
